import { createClient } from '@supabase/supabase-js';

import { createBrowserClient } from '@supabase/ssr';

import { Database } from '../database.types';
import { getSupabaseClientKeys } from '../get-supabase-client-keys';

/**
 * @name getSupabaseBrowserClient
 * @description Get a Supabase client for use in the Browser
 */
export function getSupabaseBrowserClient<GenericSchema = Database>() {
  const keys = getSupabaseClientKeys();

  return createBrowserClient<GenericSchema>(keys.url, keys.publicKey, {
    cookieOptions: {
      // Mark session cookies as Secure in production (HTTPS). Gated to
      // production so local dev over http://localhost keeps working. Kept in
      // sync with the server/middleware clients to avoid attribute drift.
      secure: process.env.NODE_ENV === 'production',
    },
  });
}

/**
 * @name getSupabaseEmailChangeBrowserClient
 * @description Isolated one-shot browser client used EXCLUSIVELY for the
 * email-change initiate (`updateUser({ email })`).
 *
 * Why this exists: the default browser/server clients inherit `@supabase/ssr`'s
 * `flowType:'pkce'`. With PKCE, `updateUser({ email })` registers a
 * `code_verifier` in this browser's storage and GoTrue renders a
 * `pkce_`-prefixed `email_change` `token_hash`. The confirm handler runs
 * `verifyOtp` server-side WITHOUT that verifier, so a `pkce_` token can never be
 * completed → the change silently never commits. `flowType:'implicit'` does NOT
 * register a verifier, so GoTrue renders a plain OTP `token_hash` that the
 * server-side `verifyOtp` CAN complete – exactly like signup-confirm.
 *
 * CRITICAL – why `createClient`, NOT `@supabase/ssr`'s `createBrowserClient`:
 * `@supabase/ssr@0.10` HARDCODES `flowType:'pkce'` in `createBrowserClient` and
 * spreads it AFTER the caller's `auth` options, so a passed `flowType:'implicit'`
 * is silently overwritten back to `'pkce'` (verified in the installed source:
 * createBrowserClient.js builds `auth: { ...options?.auth, flowType:'pkce', ... }`).
 * That made the previous implicit-flow attempt a no-op – staging kept emitting
 * `pkce_` tokens and the dual-link email change never committed (e2e @journey
 * Step 11, empirically proven 2026-06-23). `@supabase/supabase-js`'s `createClient`
 * honours the option, so it actually produces a plain token (live-verified: the
 * emailed link carried a non-`pkce_` `token_hash`).
 *
 * Isolation is mandatory (live-verified, see spec K-4): a client sharing the app's
 * `storageKey` (`sb-<ref>-auth-token`) would risk a token-rewrite / autoRefresh
 * race against the live app session. We therefore:
 *  - use a dedicated `storageKey` (NEVER the app key),
 *  - `persistSession:false` + `autoRefreshToken:false` so this client persists
 *    nothing and never touches the app's stored session.
 *
 * The caller must inject the current session into this client via `setSession`
 * before calling `updateUser` (the isolated client reads NO session from the
 * shared app key).
 */
export function getSupabaseEmailChangeBrowserClient<
  GenericSchema = Database,
>() {
  const keys = getSupabaseClientKeys();

  return createClient<GenericSchema>(keys.url, keys.publicKey, {
    auth: {
      flowType: 'implicit',
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storageKey: 'sb-locari-email-change-oneshot',
    },
  });
}
